import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconAttach, IconBack } from '../components/icons';
import { useAuth } from '../auth/useAuth';
import { labelFor, useConfig } from '../context/ConfigContext';
import { formatDateTime, formatMoney } from '../utils/format';

// TS port of the source (Falah) app's pages/VendorInvoiceDetail.jsx. Views a
// VendorInvoice (Procurement's real payment-approval record — NOT the
// Programmes-module Vendor, which was deleted) via GET /vendor-invoices/:id
// (ProcurementFulfillmentController::showVendorInvoice()).
// `labelFor(lists, 'finance_method', ...)` -> `labelFor(lists,
// 'payment_method', ...)` — see ProcurementInvoiceForm.tsx's note on the
// renamed list key.
function statusBadgeClass(status: string): string {
  if (['matched_to_po', 'approved_for_payment', 'paid'].includes(status)) return 'badge-normal';
  if (['rejected', 'discrepancy_found'].includes(status)) return 'badge-important';
  return 'badge-neutral';
}

const INVOICE_STATUS_LABELS: Record<string, string> = {
  matched_to_po: 'Waiting Approval',
};

function humanize(status: string): string {
  return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

interface PaymentDocument {
  id: number;
  original_name: string;
  url: string;
}

interface Payment {
  id: number;
  payment_date: string;
  bank_reference: string | null;
  paid_by: { full_name: string } | null;
  documents: PaymentDocument[];
}

interface PaymentVoucher {
  id: number;
  status: string;
  created_at: string;
  approved_at: string | null;
  prepared_by: { full_name: string } | null;
  approved_by: { full_name: string } | null;
  payment: Payment | null;
}

interface VendorInvoiceRecord {
  id: number;
  invoice_number: string;
  invoice_amount: number;
  invoice_date: string;
  due_date: string | null;
  category: string | null;
  status: string;
  payment_method: string;
  vendor_name: string;
  created_at: string;
  created_by: { full_name: string } | null;
  purchase_order: { id: number; po_number: string; vendor_name: string; amount: number } | null;
  payment_voucher: PaymentVoucher | null;
  discrepancy_reasons: string[];
}

const DISCREPANCY_MESSAGES: Record<string, string> = {
  amount_mismatch: 'Invoice amount does not match the purchase order amount.',
  no_goods_receipt: 'No goods have been received against this purchase order yet.',
};

interface ApprovalRow {
  key: string;
  step: string;
  by: string | undefined;
  decision: string;
  date: string | null | undefined;
}

export function VendorInvoiceDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can } = useAuth();
  const { settings, lists } = useConfig();
  const fmtMoney = (n: number) => formatMoney(n, settings.currency);
  const [inv, setInv] = useState<VendorInvoiceRecord | null>(null);
  const [actionError, setActionError] = useState('');
  const paymentFileRef = useRef<HTMLInputElement | null>(null);

  const refresh = async () => {
    const res = await api.get<VendorInvoiceRecord>(`/vendor-invoices/${id}`);
    setInv(res.data);
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const runAction = async (fn: () => Promise<unknown>) => {
    setActionError('');
    try {
      await fn();
      await refresh();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Something went wrong.');
    }
  };

  const decideInvoice = (decision: 'approved' | 'rejected') =>
    runAction(() => api.post(`/vendor-invoices/${id}/approve`, { decision }));

  const approvePayment = () => runAction(() => api.post(`/payment-vouchers/${inv?.payment_voucher?.id}/approve`));

  const pickPaymentDocument = () => paymentFileRef.current?.click();

  const onPaymentDocumentSelected = (e: ChangeEvent<HTMLInputElement>, paymentId: number) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const data = new FormData();
    data.append('file', file);
    runAction(() =>
      api.post(`/procurement-payments/${paymentId}/documents`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    );
  };

  if (!inv) return null;

  const po = inv.purchase_order;
  const pv = inv.payment_voucher;
  const payment = pv?.payment;
  const canApprovePayments = can('procurement.approve');

  const approvals: ApprovalRow[] = [
    {
      key: 'created',
      step: 'Payment created',
      by: inv.created_by?.full_name,
      decision: 'Created',
      date: inv.created_at,
    },
  ];
  if (pv) {
    approvals.push(
      pv.approved_by
        ? {
            key: 'approved',
            step: 'Payment approved',
            by: pv.approved_by?.full_name,
            decision: humanize(pv.status),
            date: pv.approved_at,
          }
        : {
            key: 'prepared',
            step: 'Payment voucher prepared',
            by: pv.prepared_by?.full_name,
            decision: 'Prepared',
            date: pv.created_at,
          }
    );
    if (payment) {
      approvals.push({
        key: 'paid',
        step: 'Payment recorded',
        by: payment.paid_by?.full_name,
        decision: 'Paid',
        date: payment.payment_date,
      });
    }
  }

  return (
    <>
      <header className="page-header">
        <div className="page-header-actions">
          <IconButton icon={IconBack} label="Back to list" onClick={() => navigate('/procurement/payments')} />
          {payment && (
            <>
              <IconButton icon={IconAttach} label="Attach payment proof" onClick={pickPaymentDocument} />
              <input
                ref={paymentFileRef}
                type="file"
                accept="application/pdf,image/png,image/jpeg,image/webp"
                onChange={(e) => onPaymentDocumentSelected(e, payment.id)}
                hidden
              />
            </>
          )}
          {pv?.status === 'pending_approval' && canApprovePayments && (
            <button type="button" className="btn btn-primary" onClick={approvePayment}>
              Approve Payment
            </button>
          )}
          {pv?.status === 'approved_for_payment' && !payment && (
            <Link className="btn btn-primary" to={`/procurement/payments/voucher/${pv.id}/pay`}>
              Pay
            </Link>
          )}
          {['matched_to_po', 'discrepancy_found'].includes(inv.status) && canApprovePayments && (
            <>
              <button type="button" className="btn btn-danger-ghost" onClick={() => decideInvoice('rejected')}>
                Reject
              </button>
              <button type="button" className="btn btn-primary" onClick={() => decideInvoice('approved')}>
                Approve for payment
              </button>
            </>
          )}
        </div>
      </header>

      {actionError && (
        <div className="alert alert-error" style={{ marginBottom: 16 }}>
          {actionError}
        </div>
      )}

      <div className="card">
        <div className="detail-summary">
          <div>
            <div className="detail-summary-title">{inv.invoice_number}</div>
            <span className={`badge ${statusBadgeClass(inv.status)}`}>
              {INVOICE_STATUS_LABELS[inv.status] ?? humanize(inv.status)}
            </span>
            <div className="detail-summary-amount">{fmtMoney(inv.invoice_amount)}</div>
            <div className="detail-summary-service">{po?.vendor_name ?? inv.vendor_name}</div>
          </div>
        </div>

        <div className="detail-grid">
          <div className="detail-item">
            <span className="detail-item-label">Invoice number</span>
            <span className="detail-item-value">{inv.invoice_number}</span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Order No</span>
            {po ? (
              <Link className="detail-item-value" to={`/procurement/orders/${po.id}`}>
                {po.po_number}
              </Link>
            ) : (
              <span className="detail-item-value"></span>
            )}
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Category</span>
            <span className={`detail-item-value${inv.category ? '' : ' empty'}`}>{inv.category || 'Not set'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Invoice date</span>
            <span className="detail-item-value">{inv.invoice_date}</span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Due date</span>
            <span className="detail-item-value">{inv.due_date}</span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Payment method</span>
            <span className="detail-item-value">{labelFor(lists, 'payment_method', inv.payment_method)}</span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Date paid</span>
            <span className="detail-item-value">{payment?.payment_date}</span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Payment reference</span>
            <span className="detail-item-value">{payment?.bank_reference}</span>
          </div>
        </div>

        {inv.status === 'discrepancy_found' &&
          inv.discrepancy_reasons.map((reason) => (
            <p key={reason} className="alert alert-error">
              {DISCREPANCY_MESSAGES[reason] ?? reason}
              {reason === 'amount_mismatch' && po && ` (PO amount: ${fmtMoney(po.amount)}).`}
            </p>
          ))}

        {payment?.documents && payment.documents.length > 0 && (
          <div className="detail-item detail-item-full">
            <span className="detail-item-label">Payment proof</span>
            <ul className="simple-list">
              {payment.documents.map((doc) => (
                <li key={doc.id}>
                  <a href={doc.url} target="_blank" rel="noreferrer">
                    {doc.original_name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="card">
        <h2>History</h2>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Step</th>
                <th>By</th>
                <th>Decision</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {approvals.map((a) => (
                <tr key={a.key}>
                  <td>{a.step}</td>
                  <td>{a.by}</td>
                  <td>{a.decision}</td>
                  <td className="muted">{a.date ? formatDateTime(a.date) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
